import {
  Activity,
  Archive,
  BellRing,
  Building2,
  ClipboardList,
  Contact,
  FileSearch,
  GitBranchPlus,
  CreditCard,
  DatabaseBackup,
  LayoutDashboard,
  LifeBuoy,
  Mail,
  Package,
  RefreshCw,
  ServerCog,
  ShieldCheck,
  UserSquare2,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { capabilityAllows, ownerMenuFor, type OwnerMenuKey, type PlatformCapabilityCode } from '@his/shared';
import { useAuth } from '@/lib/auth';

export const OWNER_NAV_GROUPS = ['Home', 'Customers', 'Operations', 'Commercial', 'Distribution', 'Platform'] as const;
export type OwnerNavGroup = (typeof OWNER_NAV_GROUPS)[number];

export interface OwnerNavItem {
  key: OwnerMenuKey;
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  group: OwnerNavGroup;
}

export const OWNER_NAV: OwnerNavItem[] = [
  { key: 'overview', to: '/owner', label: 'Home', icon: LayoutDashboard, end: true, group: 'Home' },
  { key: 'tenants', to: '/owner/tenants', label: 'Facilities', icon: Building2, end: false, group: 'Customers' },
  { key: 'facility_requests', to: '/owner/facility-requests', label: 'Facility requests', icon: ClipboardList, end: false, group: 'Customers' },
  { key: 'branch_requests', to: '/owner/branch-requests', label: 'Branch requests', icon: GitBranchPlus, end: false, group: 'Customers' },
  { key: 'contacts', to: '/owner/contacts', label: 'Contacts', icon: Contact, end: false, group: 'Customers' },
  { key: 'offline_installs', to: '/owner/offline-installs', label: 'Offline installs', icon: ServerCog, end: false, group: 'Operations' },
  { key: 'traffic', to: '/owner/traffic', label: 'Live traffic', icon: Activity, end: false, group: 'Operations' },
  { key: 'alerts', to: '/owner/alerts', label: 'Alerts', icon: BellRing, end: false, group: 'Operations' },
  { key: 'mail', to: '/owner/mail', label: 'Mail', icon: Mail, end: false, group: 'Operations' },
  { key: 'renewals', to: '/owner/renewals', label: 'Renewals', icon: RefreshCw, end: false, group: 'Commercial' },
  { key: 'tickets', to: '/owner/tickets', label: 'Tickets', icon: LifeBuoy, end: false, group: 'Commercial' },
  { key: 'plans', to: '/owner/plans', label: 'Packages & add-ons', icon: CreditCard, end: false, group: 'Commercial' },
  { key: 'person_changes', to: '/owner/person-changes', label: 'Patient changes', icon: UserSquare2, end: false, group: 'Commercial' },
  { key: 'releases', to: '/owner/releases', label: 'Releases', icon: Package, end: false, group: 'Distribution' },
  { key: 'owners', to: '/owner/owners', label: 'Owner accounts', icon: ShieldCheck, end: false, group: 'Platform' },
  { key: 'staff', to: '/owner/staff', label: 'Platform staff', icon: Users, end: false, group: 'Platform' },
  { key: 'backups', to: '/owner/backups', label: 'Database backups', icon: DatabaseBackup, end: false, group: 'Platform' },
  { key: 'deletion_backups', to: '/owner/deletion-backups', label: 'Deletion backups', icon: Archive, end: false, group: 'Platform' },
  { key: 'owner_audit', to: '/owner/audit', label: 'Owner audit', icon: FileSearch, end: false, group: 'Platform' },
];

export function ownerNavFor(role: string | null | undefined): OwnerNavItem[] {
  const allowed = new Set(ownerMenuFor(role));
  return OWNER_NAV.filter((item) => allowed.has(item.key));
}

export function ownerNavGroups(items: OwnerNavItem[]): { group: OwnerNavGroup; items: OwnerNavItem[] }[] {
  return OWNER_NAV_GROUPS.map((group) => ({ group, items: items.filter((item) => item.group === group) })).filter((entry) => entry.items.length > 0);
}

export const PLATFORM_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  platform_admin: 'Platform admin',
  support_agent: 'Support',
  platform_finance: 'Finance',
};

export function useOwnerCapability(code: PlatformCapabilityCode): boolean {
  const { user } = useAuth();
  return capabilityAllows(code, user?.platformRole);
}
